import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

/**
 * Review queue for spelling variants the triage folded automatically.
 *
 * The fold is deterministic and blind to meaning: it matches the CONSONANTAL
 * SKELETON, so "להרגעה" and "לרוגע" both reduce to "lrg" and became one query.
 * Right for typos and word order, wrong for method-vs-practitioner
 * ("אוסטאופתיה" is a subject, "אוסטאופת" is a person who belongs on /explore).
 *
 * Two things this screen refuses to blur:
 *
 * - "no volume on record" is NOT "zero demand". A folded variant never became
 *   its own keyword row, so it was never measured. Those render as "never
 *   measured", and Fetch volume is offered instead of a misleading 0.
 * - Splitting is a real write. The variant text is editable first, because the
 *   fold set often holds the typo rather than the term worth ranking.
 */

interface FoldedRow {
  canonicalKeywordId: string;
  canonicalKeyword: string;
  canonicalVolume: number | null;
  variant: string;
  volumeKnown: boolean;
  variantVolume: number | null;
  suspect: boolean;
  decision: "keep" | "split" | null;
  splitKeywordId: string | null;
}

type Filter = "suspect" | "undecided" | "all" | "decided";

const key = (r: FoldedRow) => `${r.canonicalKeywordId}::${r.variant}`;

export function FoldedVariantsPage() {
  const qc = useQueryClient();
  const [filter, setFilter] = useState<Filter>("suspect");
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [fetchMsg, setFetchMsg] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<{ rows: FoldedRow[]; counts: Record<string, number> }>({
    queryKey: ["folded-variants"],
    queryFn: async () => {
      const r = await fetch("/api/folded-variants", { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.json();
    },
  });

  const post = async (path: string, body: unknown) => {
    const r = await fetch(path, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j?.error ?? `HTTP ${r.status}`);
    return j;
  };

  const decide = useMutation({
    mutationFn: async (v: { action: "keep" | "split" | "reset"; row: FoldedRow; splitAs?: string }) =>
      post(`/api/folded-variants/${v.action}`, {
        canonicalKeywordId: v.row.canonicalKeywordId,
        variant: v.row.variant,
        ...(v.action === "split" && v.splitAs ? { splitAs: v.splitAs } : {}),
      }),
    onMutate: () => setErr(null),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["folded-variants"] }),
    onError: (e: Error) => setErr(e.message),
  });

  // Reuses the existing manual DFS endpoint — volumes are never fetched as a
  // side effect, only when the operator asks and accepts the cost.
  const fetchVolumes = useMutation({
    mutationFn: async (keywords: string[]) => {
      setFetchMsg(null);
      return post("/api/keyword-research/fetch-volumes", { keywords, language: "he" });
    },
    onMutate: () => setErr(null),
    onSuccess: (j: { requested: number; fetched: number; cost: number }) => {
      setFetchMsg(`Fetched ${j.fetched} of ${j.requested} · cost $${(j.cost ?? 0).toFixed(4)}`);
      void qc.invalidateQueries({ queryKey: ["folded-variants"] });
    },
    onError: (e: Error) => setErr(e.message),
  });

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    if (filter === "all") return all;
    if (filter === "decided") return all.filter((r) => r.decision);
    if (filter === "undecided") return all.filter((r) => !r.decision);
    return all.filter((r) => r.suspect && !r.decision);
  }, [data, filter]);

  const selectedRows = useMemo(() => rows.filter((r) => sel.has(key(r))), [rows, sel]);

  if (isLoading) return <div className="p-6 text-sm text-slate-500">Loading folded variants…</div>;
  if (error) return <div className="p-6 text-sm text-red-600">Could not load: {String(error)}</div>;
  const c = data?.counts ?? {};

  const toggle = (k: string) =>
    setSel((s) => {
      const n = new Set(s);
      n.has(k) ? n.delete(k) : n.add(k);
      return n;
    });

  return (
    <div className="space-y-5 p-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">Folded variants</h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          The triage folds spelling variants automatically by matching the <em>consonantal skeleton</em> — Hebrew
          vowel letters are stripped, so <span dir="rtl">להרגעה</span> and <span dir="rtl">לרוגע</span> both become{" "}
          <code>lrg</code> and were treated as one query. That is right for typos and word order, and wrong when a
          <span className="font-medium"> method and a practitioner</span> share a skeleton. Those are flagged here.
        </p>
        <p className="mt-2 max-w-3xl text-xs text-slate-500">
          A folded variant never became its own keyword row, so it was never measured.{" "}
          <span className="font-medium">“Never measured” is not “zero demand”</span> — use Fetch volume to find out
          before deciding.
        </p>

        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
          {([
            ["suspect", `${c.suspect ?? 0} flagged`],
            ["undecided", `${c.undecided ?? 0} undecided`],
            ["decided", `${(c.kept ?? 0) + (c.split ?? 0)} decided`],
            ["all", `${c.total ?? 0} all`],
          ] as [Filter, string][]).map(([f, label]) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`rounded-full px-3 py-1 font-medium ${
                filter === f ? "bg-slate-800 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
              }`}
            >
              {label}
            </button>
          ))}
          <span className="text-slate-400">·</span>
          <span className="text-slate-500">{c.neverMeasured ?? 0} never measured</span>
        </div>

        {err && <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">{err}</div>}
        {fetchMsg && (
          <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
            {fetchMsg}
          </div>
        )}
      </header>

      {/* Bulk bar — only meaningful with a selection, so it says so rather than sitting dead. */}
      <div className="flex flex-wrap items-center gap-3 rounded border border-slate-200 bg-slate-50 p-3 text-xs">
        <span className="font-medium text-slate-700">
          {selectedRows.length ? `${selectedRows.length} selected` : "Select rows for bulk actions"}
        </span>
        <button
          type="button"
          disabled={!selectedRows.length || fetchVolumes.isPending}
          onClick={() => fetchVolumes.mutate(selectedRows.map((r) => r.variant))}
          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        >
          {fetchVolumes.isPending ? "Fetching…" : "Fetch volume (selected)"}
        </button>
        <button
          type="button"
          disabled={!selectedRows.length || decide.isPending}
          onClick={() => selectedRows.forEach((row) => decide.mutate({ action: "keep", row }))}
          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        >
          Keep folded (selected)
        </button>
        <span className="text-slate-400">|</span>
        <button
          type="button"
          disabled={fetchVolumes.isPending || !rows.length}
          onClick={() => fetchVolumes.mutate(rows.filter((r) => !r.volumeKnown).map((r) => r.variant).slice(0, 300))}
          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-40"
        >
          Fetch volume for all unmeasured in view
        </button>
        <span className="text-slate-500">DataForSEO is only called when you ask; cost is reported after.</span>
      </div>

      <div className="overflow-hidden rounded-lg border border-slate-200">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-8 px-3 py-2"></th>
              <th className="px-3 py-2 font-medium">Folded variant</th>
              <th className="px-3 py-2 font-medium">Volume</th>
              <th className="px-3 py-2 font-medium">Folded into (canonical)</th>
              <th className="px-3 py-2 font-medium">Decision</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map((r) => {
              const k = key(r);
              const draft = edits[k] ?? r.variant;
              return (
                <tr key={k} className={r.suspect && !r.decision ? "bg-amber-50/40 align-top" : "align-top"}>
                  <td className="px-3 py-3">
                    <input type="checkbox" checked={sel.has(k)} onChange={() => toggle(k)} />
                  </td>
                  <td className="px-3 py-3">
                    <input
                      dir="rtl"
                      value={draft}
                      onChange={(e) => setEdits((s) => ({ ...s, [k]: e.target.value }))}
                      disabled={Boolean(r.decision)}
                      className={`w-full rounded border px-2 py-1 text-sm disabled:bg-slate-50 disabled:text-slate-400 ${
                        draft !== r.variant ? "border-amber-400 bg-amber-50" : "border-slate-300"
                      }`}
                    />
                    {r.suspect && !r.decision && (
                      <div className="mt-1 text-[11px] font-medium text-amber-800">
                        Looks like a practitioner vs the method — different search intent.
                      </div>
                    )}
                    {draft !== r.variant && (
                      <div className="mt-1 text-[11px] text-slate-500">Will be split out as the edited text.</div>
                    )}
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs">
                    {r.volumeKnown ? (
                      <span className="font-medium tabular-nums text-slate-800">{r.variantVolume ?? 0}</span>
                    ) : (
                      <span className="text-slate-400">never measured</span>
                    )}
                    <button
                      type="button"
                      disabled={fetchVolumes.isPending}
                      onClick={() => fetchVolumes.mutate([r.variant])}
                      className="mt-1 block text-[11px] text-blue-700 hover:underline disabled:opacity-40"
                    >
                      Fetch
                    </button>
                  </td>
                  <td className="px-3 py-3">
                    <div dir="rtl" className="text-sm text-slate-800">{r.canonicalKeyword}</div>
                    <div className="text-[11px] text-slate-500">
                      {r.canonicalVolume !== null ? `${r.canonicalVolume} vol` : "no volume"} ·{" "}
                      <code className="text-slate-400">{r.canonicalKeywordId}</code>
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-3 text-xs">
                    {r.decision ? (
                      <div className="space-y-1">
                        <span
                          className={`rounded-full px-2 py-0.5 font-medium ${
                            r.decision === "split" ? "bg-blue-100 text-blue-700" : "bg-slate-100 text-slate-600"
                          }`}
                        >
                          {r.decision === "split" ? "split out" : "kept folded"}
                        </span>
                        {r.splitKeywordId && (
                          <code className="block text-[11px] text-slate-400">{r.splitKeywordId}</code>
                        )}
                        <button
                          type="button"
                          onClick={() => decide.mutate({ action: "reset", row: r })}
                          className="block text-[11px] text-slate-500 hover:underline"
                        >
                          undo
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ action: "split", row: r, splitAs: draft })}
                          className="rounded bg-blue-600 px-2 py-1 font-medium text-white hover:bg-blue-700 disabled:opacity-40"
                        >
                          Split out
                        </button>
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ action: "keep", row: r })}
                          className="rounded border border-slate-300 bg-white px-2 py-1 font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                        >
                          Keep folded
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-slate-500">
                  Nothing in this view.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
